<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        View Assignments
        <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="'View and manage assignments.'">
          info
        </i>
      </h2>
      <div>
        <b-btn size="md" variant="primary" :to="{ name: 'sa-create-assignment' }">
          Add Assignment
        </b-btn>
      </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search Assignments"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>
              <div class="col-lg-auto">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 200px;"
                  label="title"
                  v-model="selectedCourse"
                  :reduce="course => course.id"
                  placeholder="Filter by Course"
                  :options="courses"
                  :loading="areCoursesLoading"
                  @input="onCourseFilter"
                  :clearable="true"
                >
                  <template #option="course">
                    <div class="course-opt">
                      <span class="course-opt__title">{{ course.title }}</span>
                      <b-badge
                        v-for="(tag, i) in courseTagBadges(course)"
                        :key="i"
                        variant="info"
                        pill
                        class="course-opt__tag"
                        >{{ tag }}</b-badge
                      >
                    </div>
                  </template>
                </v-select>
              </div>
              <div class="col-lg-auto">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 200px;"
                  label="title"
                  v-model="selectedChapter"
                  :reduce="chapter => chapter.id"
                  placeholder="Filter by Lecture"
                  :options="chapters"
                  :loading="areChaptersLoading"
                  :disabled="!selectedCourse"
                  @input="onChapterFilter"
                  :clearable="true"
                >
                </v-select>
              </div>
              <div class="col-lg-auto">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 200px;"
                  label="title"
                  v-model="selectedProgram"
                  :reduce="program => program.id"
                  placeholder="Filter by Program"
                  :options="allPrograms"
                  :loading="areProgramsLoading"
                  @input="onProgramFilter"
                  :clearable="true"
                >
                </v-select>
              </div>
            </div>

            <b-table
              :fields="tableColumns"
              :items="assignments"
              v-if="assignments.length"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
              no-local-sorting
              @sort-changed="onSortChange"
              @row-clicked="rowClicked"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>

              <template #cell(title)="data">
                <strong>{{ data.item.title }}</strong>
              </template>

              <template #cell(description)="data">
                <div class="text-truncate" style="max-width: 300px">
                  {{
                    data.item.description && data.item.description.length > 100
                      ? data.item.description.substring(0, 100) + '...'
                      : data.item.description || '-'
                  }}
                </div>
              </template>

              <template #cell(program)="data">
                {{ data.item.program ? data.item.program.title : '-' }}
              </template>

              <template #cell(assignment_type)="data">
                <b-badge v-if="data.item.assignment_type === 'scenario'" variant="info">
                  Scenario
                </b-badge>
                <b-badge v-else variant="light">
                  Standard
                </b-badge>
              </template>

              <template #cell(is_graded)="data">
                <b-badge v-if="data.item.is_graded" variant="success">
                  Graded
                </b-badge>
                <b-badge v-else variant="secondary">
                  Not Graded
                </b-badge>
              </template>

              <template #cell(weightage)="data">
                <span v-if="data.item.is_graded && data.item.weightage !== undefined">
                  {{ data.item.weightage }}%
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(actions)="data">
                <b-dropdown :boundary="'main'" no-flip variant="flush" toggle-class="text-muted" no-caret>
                  <template #button-content>
                    <i class="material-icons icon-24pt text-info">more_vert</i>
                  </template>
                  <b-dropdown-item
                    :to="{
                      name: 'sa-edit-assignment',
                      params: { id: data.item.id },
                    }"
                  >
                    <md-icon class="text-info mr-2">edit</md-icon>
                    Edit Assignment
                  </b-dropdown-item>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item @click="confirmDeleteAssignment(data.item.id)">
                    <md-icon class="text-danger mr-2">delete</md-icon>
                    Delete Assignment
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>

            <div v-if="!isLoading && !assignments.length" class="text-center p-4">
              <p>No assignments found.</p>
            </div>

            <div class="card-footer">
              <pagination
                v-model="currentPage"
                :total-rows="totalAssignments"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="assignments-table"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import Pagination from '../../../components/Ui/Pagination.vue';
import persistListFilters from '@/mixins/persist-list-filters';

export default {
  components: {
    Pagination,
  },
  extends: Page,
  mixins: [persistListFilters],
  filterKeys: ['searchTerm', 'selectedProgram', 'selectedCourse', 'selectedChapter', 'sortBy', 'sortDesc', 'currentPage'],

  data() {
    return {
      title: 'View Assignments',
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalAssignments: 0,
      searchTerm: '',
      sortBy: 'title',
      sortDesc: false,
      assignments: [],
      allPrograms: [],
      areProgramsLoading: false,
      selectedProgram: null,
      courses: [],
      areCoursesLoading: false,
      selectedCourse: null,
      chapters: [],
      areChaptersLoading: false,
      selectedChapter: null,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    tableColumns() {
      return [
        { key: 'actions', label: '', class: 'text-left', sortable: false },
        { key: 'title', label: 'Title', sortable: true },
        { key: 'assignment_type', label: 'Type', sortable: false, class: 'text-center' },
        { key: 'program', label: 'Program', sortable: false, thStyle: { minWidth: '150px' } },
        { key: 'is_graded', label: 'Graded', sortable: false, class: 'text-center' },
        { key: 'weightage', label: 'Weightage', sortable: false, class: 'text-center' },
        { key: 'description', label: 'Description', sortable: false },
        { key: 'total_tasks', label: 'Tasks', sortable: false, class: 'text-center' },
      ];
    },
  },

  methods: {
    ...mapActions('assignments', ['getAssignments', 'deleteAssignment']),
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('courses', ['getLmsCourses']),
    ...mapActions('chapters', ['getChapters']),

    onPageChange(pageNum) {
      this.currentPage = pageNum;
      this.fetchAssignments();
    },

    async confirmDeleteAssignment(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this assignment?', {
        title: 'Confirm Delete',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel',
        centered: true,
      });

      if (confirmed) {
        await this.onDeleteAssignment(id);
      }
    },

    async onDeleteAssignment(id) {
      try {
        await this.deleteAssignment(id);
        this.makeToast({ variant: 'success', msg: 'Assignment deleted successfully' });
        this.fetchAssignments();
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to delete assignment' });
      }
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },

    async fetchAssignments() {
      this.isLoading = true;
      try {
        const params = {
          pagination: true,
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          ...(this.searchTerm && { search: this.searchTerm }),
          ordering: this.sortDesc ? `-${this.sortBy}` : this.sortBy,
          ...(this.selectedProgram && { program: this.selectedProgram }),
          ...(this.selectedCourse && { lessons__chapter__course: this.selectedCourse }),
          ...(this.selectedChapter && { lessons__chapter: this.selectedChapter }),
        };

        const response = await this.getAssignments(params);
        this.assignments = response.data.results ? response.data.results : response.data;
        this.totalAssignments = response.data.count || response.data.length;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to fetch assignments' });
      } finally {
        this.isLoading = false;
      }
    },

    rowClicked(row) {
      this.$router.push({
        name: 'sa-edit-assignment',
        params: { id: row.id },
      });
    },

    onSortChange(context) {
      this.sortBy = context.sortBy;
      this.sortDesc = context.sortDesc;
      this.fetchAssignments();
    },

    onSearch() {
      this.currentPage = 1;
      this.debouncedAssignmentSearch();
    },

    onProgramFilter() {
      this.currentPage = 1;
      this.fetchAssignments();
    },

    onCourseFilter() {
      this.currentPage = 1;
      this.selectedChapter = null;
      this.fetchChapters();
      this.fetchAssignments();
    },

    courseTagBadges(course) {
      const tags = (course && course.custom_tags) || [];
      if (tags.length <= 2) return tags;
      return [...tags.slice(0, 2), `+${tags.length - 2}`];
    },

    onChapterFilter() {
      this.currentPage = 1;
      this.fetchAssignments();
    },

    async fetchCourses() {
      this.areCoursesLoading = true;
      try {
        const response = await this.getLmsCourses({ page_size: 100, ordering: 'title' });
        this.courses = response.data || [];
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areCoursesLoading = false;
    },

    async fetchChapters() {
      if (!this.selectedCourse) {
        this.chapters = [];
        return;
      }
      this.areChaptersLoading = true;
      try {
        const res = await this.getChapters({ course__id: this.selectedCourse, limit: 100 });
        this.chapters = (res.data && res.data.results) || res.data || [];
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areChaptersLoading = false;
    },

    debouncedAssignmentSearch: debounce(function() {
      this.fetchAssignments();
    }, 500),
  },

  async created() {
    this.fetchAssignments();
    await this.fetchPrograms();
    await this.fetchCourses();
    // A restored course filter needs its lectures loaded so the chapter
    // dropdown shows the persisted selection.
    if (this.selectedCourse) {
      this.fetchChapters();
    }
  },
};
</script>

<style scoped>
.clickable-item {
  cursor: pointer;
}

::v-deep .vs__dropdown-option {
  white-space: normal;
}

.course-opt {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.course-opt__title {
  font-weight: 600;
}
.course-opt__tag {
  font-weight: 500;
  font-size: 0.65rem;
  letter-spacing: 0.2px;
}
</style>
